import { useCallback, useRef, useState } from 'react';
import {
    clientXToTime,
    computeResizeLeftResult,
    computeResizeRightResult,
    sortSegmentsByStart,
    type IndexedSegment,
} from './subtitleTimelineResizeMath';

export type SubtitleResizeCompletePayload = {
    subtitleId: number;
    startSec: number;
    endSec: number;
};

export type UseSubtitleResizeOptions = {
    subtitleId: number;
    /** Timeline total duration in seconds (same source as TimelineEditor.totalTime). */
    getTotalTime: () => number;
    /** All `.segment` roots in the same track (including this one), for neighbor / ordering. */
    getSiblingSegmentRoots: () => HTMLElement[];
    /** Called on every valid pointer move (after rAF); update layout / dataset only, no API. */
    onPreviewLayout: () => void;
    /** 指针松开后调用；可返回 Promise，完成前应保持 isResizing 以防 React 用旧 model 覆盖 dataset */
    onResizeComplete: (payload: SubtitleResizeCompletePayload) => void | Promise<void>;
};

/**
 * Headless resize interaction: pointer capture + rAF, DOM/dataset updates outside React state.
 * Business / API stays in onResizeComplete (page or TimelineEditor).
 */
function capSegmentZIndexBelowPlayhead(): number {
    const playhead = document.getElementById('playhead');
    const pz = playhead ? Number.parseInt(window.getComputedStyle(playhead).zIndex || '10', 10) : 10;
    return Number.isFinite(pz) ? Math.max(0, pz - 1) : 9;
}

export function useSubtitleResize(segmentRef: React.RefObject<HTMLElement | null>, options: UseSubtitleResizeOptions) {
    const { subtitleId, getTotalTime, getSiblingSegmentRoots, onPreviewLayout, onResizeComplete } = options;

    const [isResizing, setIsResizing] = useState(false);
    const rafRef = useRef<number | null>(null);
    const activePointerIdRef = useRef<number | null>(null);

    const onResizePointerDown = useCallback(
        (event: React.PointerEvent, side: 'left' | 'right') => {
            const root = segmentRef.current;
            if (!root) return;

            event.preventDefault();
            event.stopPropagation();

            const trackContent = root.closest('.track-content') as HTMLElement | null;
            if (!trackContent) return;

            const totalTime = getTotalTime();
            if (totalTime <= 0) return;

            // 重叠轨：未先点整条 segment 时 z-index 可能较低，手柄被邻块盖住；拖边前提顶到播放头之下
            const savedZIndex = root.style.zIndex;
            const zCap = capSegmentZIndexBelowPlayhead();
            let topZ = 1;
            for (const el of getSiblingSegmentRoots()) {
                const z = Number.parseInt(el.style.zIndex || '1', 10);
                if (Number.isFinite(z) && z > topZ) topZ = z;
            }
            root.style.zIndex = String(Math.min(topZ + 1, zCap));

            setIsResizing(true);
            activePointerIdRef.current = event.pointerId;

            try {
                root.setPointerCapture(event.pointerId);
            } catch {
                /* ignore */
            }

            document.body.style.userSelect = 'none';

            const buildSorted = (): { sorted: IndexedSegment[]; index: number } => {
                const roots = getSiblingSegmentRoots();
                const indexed: IndexedSegment[] = roots.map(element => ({
                    element,
                    start: parseFloat(element.dataset.start || '0'),
                    end: parseFloat(element.dataset.end || '0'),
                }));
                const sorted = sortSegmentsByStart(indexed);
                const index = sorted.findIndex(s => s.element === root);
                return { sorted, index };
            };

            const originalStart = parseFloat(root.dataset.originalStart || root.dataset.start || '0');
            const originalEnd = parseFloat(root.dataset.originalEnd || root.dataset.end || '0');
            const originalSpeed = parseFloat(root.dataset.originalSpeed || '1');

            const applyMove = (clientX: number) => {
                const trackRect = trackContent.getBoundingClientRect();
                const newTime = clientXToTime(clientX, trackRect, totalTime);
                const { sorted, index } = buildSorted();
                if (index < 0) return;

                let next: { start: number; end: number } | null = null;

                if (side === 'left') {
                    const endTime = parseFloat(root.dataset.end || '0');
                    next = computeResizeLeftResult({
                        newTimeFromPointer: newTime,
                        endTime,
                        currentIndex: index,
                        sortedSegments: sorted,
                        originalStart,
                        originalEnd,
                        originalSpeed,
                    });
                } else {
                    const startTime = parseFloat(root.dataset.start || '0');
                    next = computeResizeRightResult({
                        newTimeFromPointer: newTime,
                        startTime,
                        currentIndex: index,
                        sortedSegments: sorted,
                        originalStart,
                        originalEnd,
                        originalSpeed,
                    });
                }

                if (!next) return;

                root.dataset.start = next.start.toFixed(3);
                root.dataset.end = next.end.toFixed(3);
                onPreviewLayout();
            };

            const onPointerMove = (ev: PointerEvent) => {
                if (ev.pointerId !== activePointerIdRef.current) return;
                if (rafRef.current !== null) {
                    cancelAnimationFrame(rafRef.current);
                }
                rafRef.current = requestAnimationFrame(() => {
                    rafRef.current = null;
                    applyMove(ev.clientX);
                });
            };

            const end = (ev: PointerEvent | null) => {
                if (ev && ev.pointerId !== activePointerIdRef.current) return;

                if (rafRef.current !== null) {
                    cancelAnimationFrame(rafRef.current);
                    rafRef.current = null;
                }

                root.removeEventListener('pointermove', onPointerMove);
                root.removeEventListener('pointerup', end);
                root.removeEventListener('pointercancel', end);

                try {
                    if (activePointerIdRef.current !== null) {
                        root.releasePointerCapture(activePointerIdRef.current);
                    }
                } catch {
                    /* ignore */
                }

                activePointerIdRef.current = null;
                document.body.style.userSelect = '';

                if (savedZIndex) {
                    root.style.zIndex = savedZIndex;
                } else {
                    root.style.removeProperty('z-index');
                }

                const startSec = parseFloat(root.dataset.start || '0');
                const endSec = parseFloat(root.dataset.end || '0');

                void (async () => {
                    try {
                        await Promise.resolve(onResizeComplete({ subtitleId, startSec, endSec }));
                    } finally {
                        setIsResizing(false);
                    }
                })();
            };

            root.addEventListener('pointermove', onPointerMove);
            root.addEventListener('pointerup', end);
            root.addEventListener('pointercancel', end);
        },
        [getSiblingSegmentRoots, getTotalTime, onPreviewLayout, onResizeComplete, segmentRef, subtitleId]
    );

    return { onResizePointerDown, isResizing };
}
